import urlModule from 'url';
import React, { ReactNode, useRef } from 'react';
import { Link } from 'react-router';

// Components
import { Tooltip, TooltipPlacement } from '../Tooltip';
import { HyperlinkTarget, HyperlinkType } from './Hyperlink.enums';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { SVGIcon } from '../SVG';

type HyperlinkType = {
  id?: string;
  reference?: any;
  className?: string;
  title?: string | null;
  rel?: string | null;
  role?: string;
  target?: string;
  tooltipPlacement?: TooltipPlacement;
  to: string;
  children?: string | ReactNode | string[] | ReactNode[];
  routing?: boolean;
  external?: boolean;
  tabIndex?: number;
  hideTooltipFromScreenReader?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaControls?: string;
  ariaCurrent?: boolean | 'false' | 'true' | 'page' | 'step' | 'location' | 'date' | 'time' | undefined;
  ariaDescribedBy?: string;
  ariaHidden?: boolean;
  ariaHaspopup?: boolean;
  style?: any;
  dataIcon?: SVGIcon;
  accessKey?: string;
  download?: boolean;

  // Events
  onClick?: any;
  onCopy?: any;
};

/**
 * Our own 'Link' component (named 'Hyperlink' since that name was already taken by React Router).
 * For now, simply passes all props down to the mentioned RR 'Link' component, which in turn passes them further down,
 * so 'className', 'title', et cetera can just be set on this component as usual.
 */
function Hyperlink({
  id,
  reference,
  className,
  title,
  rel,
  role,
  target,
  tooltipPlacement,
  to,
  children,
  routing = true,
  external = false,
  tabIndex,
  hideTooltipFromScreenReader,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  ariaCurrent,
  ariaDescribedBy,
  ariaHidden,
  ariaHaspopup,
  style,
  dataIcon,
  accessKey,
  onClick,
  onCopy,
  download,
}: HyperlinkType) {
  const tmpRef = useRef(null);
  const linkRef = reference || tmpRef;
  const { userInfo } = useAppContext();

  const getClass = () => {
    const cssClass = [];

    if (className) {
      cssClass.push(className);
    }

    return cssClass.join(' ') || undefined;
  };

  /**
   * Returns an id, can be null.
   * Refenreces the description for links that open in new window.
   * target="_self" opens in the same window!
   * See <ExternalLinkDescription> as well.
   *
   * This needs refactoring.
   * - described by should not be dependent on target but on a prop.
   * - opens-in-new-window was the first time it was used, but now links can also contain
   *   other protocols like tel: or mailto:, so this is is also wrong.
   */
  const getAriaDescribedBy = () => {
    if (ariaDescribedBy) {
      return ariaDescribedBy;
    }

    return target && target !== HyperlinkTarget.SELF ? 'opens-in-new-window' : undefined;
  };

  const hideTooltipFromScreenReaderFunc = () => {
    // when we have an external link, the tooltip should be hidden from screen readers
    // otherwise Tippy will remove the aria-describedby, that was set by getAriaDescribedBy()
    // the text should permanently refer to the static <ExternalLinkDescription>, see <GenericPage>
    if (target) {
      return target !== HyperlinkTarget.SELF;
    }
    return hideTooltipFromScreenReader;
  };

  // see: https://www.owasp.org/index.php/Reverse_Tabnabbing
  const updateRelAttribute = (useLink: boolean): string | undefined => {
    const relationship = rel ? new Set(rel.split(' ')) : new Set();

    if (target) {
      relationship.add(HyperlinkType.NO_REFERRER);
      if (target === HyperlinkTarget.BLANK) {
        relationship.add(HyperlinkType.NO_OPENER);
      }
    }

    if (external === true && !useLink) {
      relationship.add(HyperlinkType.EXTERNAL);
    }

    let result: string | undefined;
    relationship.forEach((item) => {
      // external is not allowed for <Link>
      if (useLink && item === HyperlinkType.EXTERNAL) {
        return;
      }

      if (result === undefined) {
        result = item as string;
      } else {
        result += ` ${item}`;
      }
    });

    return result;
  };

  const buildLink = (routingProp: boolean) => {
    // CLIENT_SIDE_ROUTING will be set by process_env
    const useLink = routingProp;
    const LinkProperties = {
      className: getClass(),
      title: title || undefined,
      rel: updateRelAttribute(useLink),
      role: role || undefined,
      target,
      id,
    };

    return (
      <Tooltip
        placement={tooltipPlacement}
        hideTooltipFromScreenReader={hideTooltipFromScreenReaderFunc()}
        attachTo={linkRef}
      >
        {useLink
          ? (
        // routed link
        // if used correctly and wisely, accesskeys are a11y enhancement
        // eslint-disable-next-line jsx-a11y/no-access-key
              <Link
                {...LinkProperties}
                to={to}
                ref={linkRef}
                tabIndex={tabIndex}
                aria-label={ariaLabel}
                aria-expanded={ariaExpanded}
                aria-controls={ariaControls}
                aria-current={ariaCurrent}
                aria-describedby={getAriaDescribedBy()}
                aria-hidden={ariaHidden}
                aria-haspopup={ariaHaspopup}
                onClick={onClick}
                onCopy={onCopy}
                style={style}
                accessKey={userInfo?.settings?.accessKeysEnabled ? accessKey : undefined}
                data-icon={dataIcon}
                download={download}
              >
                {children}
              </Link>
            )
          : (
        // regular link (no routing)
        // if used correctly and wisely, accesskeys are a11y enhancement
        // eslint-disable-next-line jsx-a11y/no-access-key
              <a
                {...LinkProperties}
                tabIndex={tabIndex}
                ref={linkRef}
                href={to}
                aria-label={ariaLabel}
                aria-expanded={ariaExpanded}
                aria-controls={ariaControls}
                aria-current={ariaCurrent}
                aria-describedby={getAriaDescribedBy()}
                aria-hidden={ariaHidden}
                aria-haspopup={ariaHaspopup}
                onClick={onClick}
                onCopy={onCopy}
                style={style}
                accessKey={userInfo?.settings?.accessKeysEnabled ? accessKey : undefined}
                data-icon={dataIcon}
                download={download}
              >
                {children}
              </a>
            )}
      </Tooltip>
    );
  };

  // caution: make sure not to use the module 'url-parse'!
  // It completes missing URL attributes from the browser location on the client-side
  let url;
  try {
    url = urlModule.parse(to);
  } catch {
    return <StatusInfoError />;
  }

  // prevent react-router from transforming absolute URLs (incl. mailto/tel links) and hash-only URLs like #123
  const isAbsoluteOrFragment = url.protocol || url.host || to.startsWith('#') || external;
  return buildLink(!isAbsoluteOrFragment && routing);
}

// shouldComponentUpdate(nextProps) {
//   return !(
//     to === nextProps.to &&
//     tooltipPlacement === nextProps.tooltipPlacement &&
//     children === nextProps.children &&
//     userInfo.settings?.accessKeysEnabled === nextProps.userInfo.settings?.accessKeysEnabled
//   );
// }
export default React.memo(Hyperlink);
